import objectStorageService from '@clarity-website/utils/object-storage-service';

interface MessageBannerReportMapping {
  [reportGroupId: string]: number;
}

interface MessageBannerMapping {
  [messageBannerId: string]: MessageBannerReportMapping;
}

/**
 * interface for message banner ttl operations
 */
export interface MessageBannerStorage {
  /**
   * Gets the complete mapping from storage
   */
  getMapping(): MessageBannerMapping;

  /**
   * Sets the complete mapping in storage
   */
  setMapping(mapping: MessageBannerMapping): void;

  /**
   * Adds a ttl for a message banner in a report group
   */
  addBannerTtl(messageBannerId: string, reportGroupId: string): void;

  /**
   * Adds a ttl for a message banner in a report group
   */
  getBannerTtl(messageBannerId: string, reportGroupId: string): number | null;

  /**
   * Adds a ttl for a message banner in a report group
   */
  removeBannerTtl(messageBannerId: string, reportGroupId: string): void;
}

/**
 * Creates a transient storage implementation with the specified storage key
 * This factory function allows for different storage behaviors while sharing the same interface
 */
export function createMessageBannerStorage(): MessageBannerStorage {
  const storage = objectStorageService('message-banners-');

  return {
    // Additive mode methods
    getMapping(): MessageBannerMapping {
      return storage.getObject<MessageBannerMapping>('report-level') || {};
    },

    setMapping(mapping: MessageBannerMapping): void {
      storage.setObject('report-level', mapping);
    },

    addBannerTtl(messageBannerId: string, reportGroupId: string): void {
      let mapping = this.getMapping();
      if (!mapping) {
        mapping = {};
      }

      if (!mapping[messageBannerId]) {
        mapping[messageBannerId] = {};
      }

      const twoDaysInMS = 60 * 60 * 48 * 1000;

      mapping[messageBannerId][reportGroupId] =
        new Date().getTime() + twoDaysInMS;

      this.setMapping(mapping);
    },

    getBannerTtl(
      messageBannerId: string,
      reportGroupId: string,
    ): number | null {
      const mapping = this.getMapping();

      return mapping?.[messageBannerId]?.[reportGroupId] ?? null;
    },

    removeBannerTtl(messageBannerId: string, reportGroupId: string): void {
      const mapping = this.getMapping();
      if (!mapping?.[messageBannerId]?.[reportGroupId]) {
        return;
      }

      delete mapping[messageBannerId][reportGroupId];

      this.setMapping(mapping);
    },
  };
}

export const bannerStorage = createMessageBannerStorage();
